import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useCreateSection, useDeleteSection, useDeleteLesson } from '@/api/course-builder';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { SectionItem } from './section-item';
import type { BuilderSection, BuilderLesson } from '@/types';

type Props = {
  sections: BuilderSection[];
  isDraft: boolean;
  courseId: number;
};

export function SectionList({ sections, isDraft, courseId }: Props) {
  const createSectionMut = useCreateSection();
  const deleteSectionMut = useDeleteSection();
  const deleteLessonMut = useDeleteLesson();

  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [showNewSection, setShowNewSection] = useState(false);

  const [deletingSection, setDeletingSection] = useState<BuilderSection | null>(null);
  const [deletingLesson, setDeletingLesson] = useState<{
    lesson: BuilderLesson;
    sectionId: number;
  } | null>(null);

  const toggleSection = (sectionId: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const handleCreateSection = () => {
    if (!newSectionTitle.trim()) return;
    createSectionMut.mutate(
      { courseId, data: { title: newSectionTitle.trim() } },
      {
        onSuccess: () => {
          setNewSectionTitle('');
          setShowNewSection(false);
        },
      },
    );
  };

  const handleDeleteSection = () => {
    if (!deletingSection) return;
    deleteSectionMut.mutate(
      { courseId, sectionId: deletingSection.id },
      { onSuccess: () => setDeletingSection(null) },
    );
  };

  const handleDeleteLesson = () => {
    if (!deletingLesson) return;
    deleteLessonMut.mutate(
      { lessonId: deletingLesson.lesson.id, courseId },
      { onSuccess: () => setDeletingLesson(null) },
    );
  };

  return (
    <div className="space-y-3">
      {sections
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((section) => (
          <SectionItem
            key={section.id}
            section={section}
            isDraft={isDraft}
            isExpanded={expandedSections.has(section.id)}
            courseId={courseId}
            onToggle={() => toggleSection(section.id)}
            onDelete={setDeletingSection}
            onDeleteLesson={(lesson, sectionId) => setDeletingLesson({ lesson, sectionId })}
          />
        ))}

      {/* Add section */}
      {isDraft && (
        <div className="mt-4">
          {showNewSection ? (
            <div className="flex items-center gap-2">
              <input
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateSection()}
                placeholder="Título de la sección"
                className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                autoFocus
              />
              <button
                onClick={handleCreateSection}
                disabled={createSectionMut.isPending}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              >
                {createSectionMut.isPending ? 'Creando...' : 'Crear'}
              </button>
              <button
                onClick={() => {
                  setShowNewSection(false);
                  setNewSectionTitle('');
                }}
                className="rounded-lg border border-border px-4 py-2 text-sm text-foreground"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowNewSection(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <Plus className="h-4 w-4" /> Agregar sección
            </button>
          )}
        </div>
      )}

      {sections.length === 0 && !showNewSection && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-12">
          <p className="text-sm font-medium text-foreground">Sin secciones</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {isDraft ? 'Agrega la primera sección a este curso' : 'Este curso no tiene secciones'}
          </p>
          {isDraft && (
            <button
              onClick={() => setShowNewSection(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              <Plus className="h-4 w-4" /> Agregar sección
            </button>
          )}
        </div>
      )}

      {/* Confirm dialogs */}
      {deletingSection && (
        <ConfirmDialog
          title="Eliminar sección"
          message={`¿Eliminar la sección "${deletingSection.title}" y todas sus lecciones?`}
          confirmLabel="Eliminar"
          variant="danger"
          isPending={deleteSectionMut.isPending}
          onConfirm={handleDeleteSection}
          onCancel={() => setDeletingSection(null)}
        />
      )}
      {deletingLesson && (
        <ConfirmDialog
          title="Eliminar lección"
          message={`¿Eliminar la lección "${deletingLesson.lesson.title}"?`}
          confirmLabel="Eliminar"
          variant="danger"
          isPending={deleteLessonMut.isPending}
          onConfirm={handleDeleteLesson}
          onCancel={() => setDeletingLesson(null)}
        />
      )}
    </div>
  );
}
